import { logger } from './logger';
// Achievement tracking using backend API
import { makeAuthenticatedRequest } from './api';

export interface Achievement {
  id: number;
  name: string;
  description: string;
  icon: string;
  category: string;
  requiredValue: number;
  statKey: string;
  unlocked: boolean;
  unlockedAt?: string | null;
}

export interface PlayerStats {
  id: number;
  userId: number;
  battlesWon: number;
  battlesLost: number;
  monstersDefeated: number;
  totalDamageDealt: number;
  itemsCrafted: number;
  recipesDiscovered: number;
  tilesExplored: number;
  materialsGathered: number;
  createdAt: string;
  updatedAt: string;
}

export const getPlayerStats = async (): Promise<PlayerStats | null> => {
  try {
    const response = await makeAuthenticatedRequest('/Achievement/stats');

    if (!response.ok) {
      logger.error('Failed to fetch player stats:', response.statusText);
      return null;
    }

    return await response.json();
  } catch (error) {
    logger.error('Error fetching player stats:', error);
    return null;
  }
};

export const getAchievements = async (): Promise<Achievement[]> => {
  try {
    const response = await makeAuthenticatedRequest('/Achievement');

    if (!response.ok) {
      logger.error('Failed to fetch achievements:', response.statusText);
      return [];
    }

    return await response.json();
  } catch (error) {
    logger.error('Error fetching achievements:', error);
    return [];
  }
};

// Note: The backend now handles stat tracking and achievement unlocking automatically
// These functions are kept for backward compatibility but are no-ops
// The backend tracks stats through BattleController and CraftingController

export const updateStats = async (updates: Partial<PlayerStats>): Promise<PlayerStats | null> => {
  logger.warn('updateStats is deprecated - stats are tracked automatically by the backend');
  return getPlayerStats();
};

export const incrementStat = async (stat: keyof PlayerStats, amount: number = 1): Promise<PlayerStats | null> => {
  logger.warn('incrementStat is deprecated - stats are tracked automatically by the backend');
  return getPlayerStats();
};

export const unlockAchievement = async (achievementId: number): Promise<Achievement | null> => {
  logger.warn('unlockAchievement is deprecated - achievements are unlocked automatically by the backend');
  const achievements = await getAchievements();
  return achievements.find(a => a.id === achievementId) || null;
};

export const checkAchievements = async (): Promise<Achievement[]> => {
  logger.warn('checkAchievements is deprecated - achievements are checked automatically by the backend');
  const achievements = await getAchievements();
  return achievements.filter(a => a.unlocked);
};
